/**
 * Admin Dashboard Data Fetching Hooks
 * 
 * Custom hooks using React Query for fetching and caching admin dashboard data.
 * Configured with 5-minute stale time and manual refresh only (no auto-refetch).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchOverviewStats,
  fetchUsersList,
  fetchTimeseriesStats,
  fetchPeriodStats,
  fetchUserDetail,
  adminChangeUserSubscription,
  adminGrantUserAccess,
} from '../utils/adminApi';

/**
 * Hook to fetch overview statistics
 * @param {string} timezone - Optional timezone name (default: 'America/New_York')
 * Returns: { total_users, total_containers, total_websites, published_websites, users_with_containers }
 */
export const useOverviewStats = (timezone = 'America/New_York') => {
  return useQuery({
    queryKey: ['admin', 'overview', timezone],
    queryFn: () => fetchOverviewStats(timezone),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch list of all users
 * Returns: { users: Array, total: number }
 */
export const useUsersList = () => {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: fetchUsersList,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch timeseries statistics for cumulative line chart
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {boolean} enabled - Whether the query should run
 */
export const useTimeseriesStats = (startDate, endDate, enabled = true) => {
  return useQuery({
    queryKey: ['admin', 'timeseries', startDate, endDate],
    queryFn: () => fetchTimeseriesStats(startDate, endDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    enabled: enabled && !!startDate && !!endDate,
  });
};

/**
 * Hook to fetch period statistics for bar charts
 * @param {string} entity - Entity type: 'users', 'containers', or 'websites'
 * @param {string} granularity - Time granularity: 'day', 'week', or 'month'
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {boolean} enabled - Whether the query should run
 */
export const usePeriodStats = (entity, granularity, startDate, endDate, enabled = true) => {
  return useQuery({
    queryKey: ['admin', 'period', entity, granularity, startDate, endDate],
    queryFn: () => fetchPeriodStats(entity, granularity, startDate, endDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    enabled: enabled && !!entity && !!granularity && !!startDate && !!endDate,
  });
};

/**
 * Hook to fetch detailed information for a specific user
 * @param {number} userId - User ID
 * @param {boolean} enabled - Whether the query should run
 */
export const useUserDetail = (userId, enabled = true) => {
  return useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: () => fetchUserDetail(userId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    enabled: enabled && !!userId,
  });
};

/**
 * Admin: change a user's subscription (Stripe-synced).
 */
export const useAdminChangeUserSubscription = (userId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminChangeUserSubscription(userId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'user', userId] });
    },
  });
};

/**
 * Admin: grant access to a user (no payment).
 */
export const useAdminGrantUserAccess = (userId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminGrantUserAccess(userId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'user', userId] });
    },
  });
};
